import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { KimMessageBody } from '../../../../backend/src/matter/subset/kim/dto/kim-message-body.dto';
import { KimThreadStatus } from '../../../../backend/src/matter/subset/thread/kim-thread-status';
import { KimThread } from '../../../../backend/src/matter/subset/thread/kim-thread.entity';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export const useSendKimMessage = (matterId?: string, subsetId?: string, threadId?: string) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: KimMessageBody) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: { message: payload.message, files: payload.files, mentions: payload.mentions },
      });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/message`,
        fetchConfig,
      );
      if (!response.ok) {
        throw new Error('Sending Kim message failed');
      }
      const result = await response.json();

      // Keep the mutation (and isLoading) alive until the refetched messages —
      // including the just-sent user message — land in the cache. The send lock
      // hands off from isSending to the message-based busy check with no gap.
      await queryClient.refetchQueries([QueryEntity.KimMessages, { subsetId, threadId }]);

      return result;
    },
    {
      // Optimistically mark the thread in-progress so status-based polling can
      // start immediately.
      onMutate: async () => {
        const threadKey = [QueryEntity.KimThread, { threadId }];
        await queryClient.cancelQueries(threadKey);
        const previousThread = queryClient.getQueryData<KimThread>(threadKey);
        if (previousThread) {
          queryClient.setQueryData<KimThread>(threadKey, {
            ...previousThread,
            status: KimThreadStatus.InProgress,
          });
        }
        return { previousThread };
      },
      onError: (_error, _vars, context) => {
        const previousThread = (context as { previousThread?: KimThread } | undefined)?.previousThread;
        if (previousThread) {
          queryClient.setQueryData([QueryEntity.KimThread, { threadId }], previousThread);
        }
        toast.error('Failed to send message. Please try again.');
      },
    },
  );
};
